import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import AddFieldModal from "../components/AddFieldModal";
import FormPreviewModal from "../components/FormPreviewModal";
import FeedbackModal from "../components/FeedbackModal";
import { useNavigate, useLocation } from "react-router-dom";
import { apiRequest } from "../api";
import EditFieldModal from "../components/EditFieldModal";

const CreateFormTemplate = () => {
    const [formName, setFormName] = useState("");
    const [formType, setFormType] = useState("undergraduate");
    const [description, setDescription] = useState("");
    const [fields, setFields] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [editingFieldData, setEditingFieldData] = useState(null);

    const [isEditing, setIsEditing] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [templateId, setTemplateId] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("token");
    const apiUrl = process.env.REACT_APP_API_BASE_URL || "https://localhost:8000";

    // Load initial data for editing
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const editId = params.get('edit');
        
        if (editId) {
            setTemplateId(editId);
            setIsEditing(true);
            loadTemplateData(editId);
        }
    }, [location.search]);

    const loadTemplateData = async (id) => {
        try {
            const data = await apiRequest(`${apiUrl}/form-templates/${id}`, "GET", null, token);
            setFormName(data.name);
            setFormType(data.type || "undergraduate");
            setDescription(data.description || "");
            setFields(
                Object.keys(data.fields || {}).map((key) => ({
                    name: key,
                    ...data.fields[key],
                }))
            );
        } catch (error) {
            console.error("Error loading template:", error);
        }
    };

    // Add a new field
    const addField = (newFields) => {
        setFields([...fields, ...newFields]);
        setShowModal(false);
    };

    // Delete a field
    const deleteField = (index) => {
        setFields(fields.filter((_, i) => i !== index));
    };

    // Move a field up
    const moveFieldUp = (index) => {
        if (index === 0) return;
        const updatedFields = [...fields];
        [updatedFields[index], updatedFields[index - 1]] = [
            updatedFields[index - 1],
            updatedFields[index],
        ];
        setFields(updatedFields);
    };

    // Move a field down
    const moveFieldDown = (index) => {
        if (index === fields.length - 1) return;
        const updatedFields = [...fields];
        [updatedFields[index], updatedFields[index + 1]] = [
            updatedFields[index + 1],
            updatedFields[index],
        ];
        setFields(updatedFields);
    };

    /** Edit an existing field */
    const editField = (index) => {
        setEditingFieldIndex(index);
        setEditingFieldData(fields[index]); // Pass the exact field data for editing
        setEditModal(true);
    };

    const saveEditedField = (updatedField) => {
        const newFields = [...fields];
        newFields[editingFieldIndex] = updatedField[0];
        setFields(newFields);
        setEditModal(false);
    };

    // Submit the form template
    const submitForm = async (e) => {
        e.preventDefault();

        const formPayload = {
            name: formName,
            description: description,
            fields: fields.reduce((acc, field) => {
                acc[field.name] = {
                    type: field.type,
                    required: field.required,
                    ...(field.options ? { options: field.options } : {}),
                    ...(field.text ? { text: field.text } : {}),
                };
                return acc;
            }, {}),
        };

        console.log("Submitting Form Template:", formPayload);
        console.log("Fields before submission:", fields);
        setIsLoading(true);
        setFeedbackMessage(null);

        try {
            const url = isEditing
                ? `${apiUrl}/form-templates/${templateId}`
                : `${apiUrl}/form-templates`;
            const method = isEditing ? "PUT" : "POST";

            await apiRequest(url, method, formPayload, token);

            setFeedbackMessage("Form template saved successfully!");
            setFields([]);
            setFormName("");
            setFormType("undergraduate");
            setDescription("");
            setIsEditing(false);
            setTemplateId(null);

            // Navigate back to form templates list after successful save
            setTimeout(() => {
                navigate("/multi-form-templates");
            }, 1500);
        } catch (error) {
            console.error("Error saving form template:", error);
            setFeedbackMessage("Failed to save form template. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="dashboard">
            <Sidebar />
            <main className="main-content">
                <header className="header component">
                    <span className="component-header">
                        {isEditing ? "Edit Form Template" : "New Form Template"}
                    </span>
                </header>
                <p><a href="/multi-dashboard">Tour Operations Dashboard</a> &gt; <a href="/multi-form-templates">Form Templates</a> &gt; {isEditing ? "Edit" : "New"}</p>
                <hr />

                <form onSubmit={submitForm}>
                    <div className="form-group">
                        <label>Form Template Name:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={formName}
                            required
                            onChange={(e) => setFormName(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="form-group">
                        <label>Description:</label>
                        <textarea
                            className="form-control"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={isLoading}
                            rows="3"
                            placeholder="Describe the purpose of this form template..."
                        />
                    </div>
                    <div className="form-group">
                        <label>Form Type:</label>
                        <select
                            className="form-control"
                            value={formType}
                            onChange={(e) => setFormType(e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="undergraduate">Undergraduate</option>
                            <option value="graduate">Graduate</option>
                            <option value="hybrid">Hybrid</option>
                        </select>
                    </div>
                    <div className="divider-text gap-top-45 gap-bottom-45">
                        <span>Form Fields</span>
                    </div>

                    <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setShowModal(true)}
                        disabled={isLoading}
                    >
                        +
                    </button>
                    <ul>
                        {fields.map((field, index) => (
                            <li className="field" key={index}>
                                {field.name} ({field.type}) {field.required ? "(*Required)" : ""} &nbsp;&nbsp;
                                <button
                                    className="btn btn-warning"
                                    type="button"
                                    onClick={() => editField(index)}
                                    disabled={isLoading}
                                >
                                    Edit
                                </button>&nbsp;
                                <button
                                    className="btn btn-primary clone-btn-right"
                                    type="button"
                                    onClick={() => moveFieldUp(index)}
                                    disabled={isLoading}
                                >
                                    ↑
                                </button>
                                &nbsp;
                                <button
                                    className="btn btn-primary clone-btn-right"
                                    type="button"
                                    onClick={() => moveFieldDown(index)}
                                    disabled={isLoading}
                                >
                                    ↓
                                </button>
                                &nbsp;
                                <button
                                    className="btn btn-default clone-btn-right delete"
                                    type="button"
                                    onClick={() => deleteField(index)}
                                    disabled={isLoading}
                                >
                                    -
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3">
                        <button
                            type="submit"
                            className="btn btn-primary mr-2"
                            disabled={isLoading}
                        >
                            {isEditing ? "Update Form Template" : "Save Form Template"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowPreview(true)}
                            disabled={isLoading}
                        >
                            Preview Form
                        </button>&nbsp;&nbsp;
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                setFields([]);
                                setFormName("");
                                setFormType("undergraduate");
                                setDescription("");
                                setIsEditing(false);
                                setTemplateId(null);
                            }}
                            disabled={isLoading}
                        >
                            Reset Form
                        </button>&nbsp;&nbsp;
                        <a
                            type="button"
                            className="btn btn-secondary"
                            href="/multi-form-templates">
                            Cancel
                        </a>
                    </div>
                </form>
                {showModal && (
                    <AddFieldModal
                        onSave={addField}
                        onClose={() => setShowModal(false)}
                    />
                )}
                {editModal && (
                    <EditFieldModal
                        field={editingFieldData}
                        fieldIndex={editingFieldIndex}
                        onSave={saveEditedField}
                        onClose={() => setEditModal(false)}
                    />
                )}
                {showPreview && (
                    <FormPreviewModal
                        fields={fields}
                        onClose={() => setShowPreview(false)}
                    />
                )}
                {feedbackMessage && (
                    <FeedbackModal
                        message={feedbackMessage}
                        onClose={() => setFeedbackMessage(null)}
                    />
                )}
            </main>
        </div>
    );
};

export default CreateFormTemplate; 